import { Fragment, useState, useCallback, useEffect, useRef } from "react";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { Card, CardBody, CardHeader, Col, Form, Row, Button, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { ArrowLeft, Search } from "react-feather";
import AsyncSelect from "react-select/async";
import { suppliersMasterAPI } from "../PharmacySuppliers/SupplierService";
import { getPreOrderDetailsAPI, updatePreOrderAPI, getPreOrderItemsAPI } from "./PreorderService";
import { toast } from "react-toastify";
import { FaPrint } from "react-icons/fa";
import PrintablePreOrder from "./printPreOrder";
import { useReactToPrint } from "react-to-print";


const UpdatePreOrder = () => {
    const { t } = useTranslation();
    const history = useNavigate();
    const { preorderId } = useParams(); // Get preorder ID from URL params
    const { loading, setLoading } = useLoader();
    const { pharmacyPOS } = usePharmacy();
    const [selectedPreOrder, setSelectedPreOrder] = useState(null);
    const [printItem, setPrintItem] = useState(null);
    const printRef = useRef();

    const [supplier, setSupplier] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchQuerySupplier, setSearchQuerySupplier] = useState('');
    const [searchQueryItem, setSearchQueryItem] = useState('');
    const [itemOptions, setItemOptions] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [multiSelectValue, setMultiSelectValue] = useState([]);
    const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
    const [printTooltipOpen, setPrintTooltipOpen] = useState(false);
    const [originalData, setOriginalData] = useState(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);
    const togglePrintTooltip = () => setPrintTooltipOpen(!printTooltipOpen);

    const [errors, setErrors] = useState({});

    // Fetch preorder details on component mount
    useEffect(() => {
        if (preorderId) {
            fetchPreOrderDetails();
        } else {
            toast.warning(t("Pre-order ID is missing"));
            history(`${process.env.PUBLIC_URL}/pre-order-list`);
        }
    }, [preorderId]);

    // Fetch preorder details
    const fetchPreOrderDetails = async () => {
        try {
            setLoading(true);
            const response = await getPreOrderDetailsAPI({ preorderId });
            const data = response.data.data;

            if (!data) {
                toast.warning(t("Pre-order not found"));
                history(`${process.env.PUBLIC_URL}/pre-order-list`);
                return;
            }

            setOriginalData(data);

            // Set supplier
            if (data.supplierId) {
                // Load supplier details
                const supplierRes = await suppliersMasterAPI({
                    search: data.supplierId,
                    clinicId: pharmacyPOS?.hospital_id
                });

                const supplierData = supplierRes.data.data?.[0];
                if (supplierData) {
                    const supplierOption = {
                        value: supplierData._id,
                        label: `${supplierData.companyName} [${supplierData.contactPerson || 'No Contact'}]`,
                        raw: supplierData
                    };
                    setSupplier(supplierOption);
                }
            }

            // Set selected items from preorderData
            if (data.preorderData && Array.isArray(data.preorderData)) {
                const items = data.preorderData.map(item => ({
                    id: item.itemId,
                    brandName: item.brandName,
                    saltName: item.saltName || "",
                    manufacturer: item.manufacturer || "",
                    package: item.package || "",
                    productForm: item.productForm || "",
                    itemId: item.itemId,
                    quantity: item.quantity ?? 1,
                    notes: "", // Default notes
                    createdAt: item.createdAt
                }));
                setSelectedItems(items);
            }

            setIsDataLoaded(true);

        } catch (error) {
            console.error("Error fetching pre-order details:", error);
            toast.warning(t("Failed to load pre-order details"));
            history(`${process.env.PUBLIC_URL}/pre-order-list`);
        } finally {
            setLoading(false);
        }
    };

    // Load Suppliers
    const loadSuppliers = async (inputValue) => {
        if (!inputValue) return [];
        try {
            const res = await suppliersMasterAPI({
                search: inputValue,
                clinicId: pharmacyPOS?.hospital_id
            });
            return (res.data.data || []).map(s => ({
                value: s._id,
                label: `${s.companyName} [${s.contactPerson || 'No Contact'}]`,
                raw: s
            }));
        } catch (err) {
            console.error("Error loading suppliers:", err);
            return [];
        }
    };

    // Load Items for selected supplier
    const loadItems = useCallback(async (inputValue) => {
        if (!supplier || !supplier.value) {
            return [];
        }

        try {
            setIsLoadingItems(true);
            const response = await getPreOrderItemsAPI({
                supplierId: supplier.value,
                search: inputValue || ""
            });

            const items = response.data.data || [];
            const options = items.map(item => ({
                value: item._id,
                label: `${item.brandName}`,
                raw: {
                    ...item,
                    price: item.regularPrice || 0
                }
            }));

            setItemOptions(options);
            return options;

        } catch (error) {
            console.error("Error loading items:", error);
            toast.warning(t("Failed to load items"));
            return [];
        } finally {
            setIsLoadingItems(false);
        }
    }, [supplier, t]);

    // Debounced search for items
    const debouncedLoadItems = useCallback(
        debounce((inputValue, callback) => {
            loadItems(inputValue).then(callback);
        }, 500),
        [loadItems]
    );

    // Load items when supplier changes
    useEffect(() => {
        if (supplier && supplier.value && isDataLoaded) {
            loadItems("");
        }
    }, [supplier, loadItems, isDataLoaded]);

    // Select Supplier
    const handleSelectSupplier = (option) => {
        setSupplier(option);
        setErrors(prev => ({ ...prev, supplier: null }));
    };

    // Select Item (Multiple)
    const handleSelectItem = (selectedOptions) => {
        if (!selectedOptions) {
            setMultiSelectValue([]);
            setSelectedItems([]);
            return;
        }

        setMultiSelectValue(selectedOptions);

        const selectedIds = new Set(selectedOptions.map(option => option.value));

        const updatedSelectedItems = selectedOptions.map(option => {
            const existingItem = selectedItems.find(item => item.id === option.value);

            if (existingItem) {
                return existingItem;
            } else {
                return {
                    id: option.value,
                    brandName: option.raw.brandName,
                    saltName: option.raw.saltName || "",
                    manufacturer: option.raw.manufacturer || "",
                    package: option.raw.package || "",
                    productForm: option.raw.productForm || "",
                    itemId: option.value,
                    quantity: 1,
                    notes: ""
                };
            }
        });

        setSelectedItems(updatedSelectedItems);
    };

    // Remove item from selected items
    const handleRemoveItem = (itemId) => {
        setSelectedItems(prev => prev.filter(item => item.id !== itemId));
        setMultiSelectValue(prev => prev.filter(option => option.value !== itemId));
    };

    // Update item quantity
    const handleQuantityChange = (itemId, value) => {
        const quantity = Math.max(1, parseInt(value) || 1);
        setSelectedItems(prev =>
            prev.map(item =>
                item.id === itemId ? { ...item, quantity } : item
            )
        );
    };

    // Update item notes
    const handleNotesChange = (itemId, value) => {
        setSelectedItems(prev =>
            prev.map(item =>
                item.id === itemId ? { ...item, notes: value } : item
            )
        );
    };

    // Handle form submission for update
    const handleUpdatePreOrder = async (e) => {
        e.preventDefault();

        // Validation
        const newErrors = {};
        if (!supplier) {
            newErrors.supplier = t("Supplier is required");
        }
        if (selectedItems.length === 0) {
            newErrors.items = t("Please select at least one item");
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.warning(t("Please fill in all required fields"));
            return;
        }

        try {
            setLoading(true);

            // Prepare update payload
            const payload = {
                preorderId: preorderId,
                supplierId: supplier.value,
                preorderData: selectedItems.map(item => ({
                    brandName: item.brandName,
                    saltName: item.saltName,
                    manufacturer: item.manufacturer,
                    package: item.package,
                    productForm: item.productForm,
                    itemId: item.itemId,
                    quantity: item.quantity
                }))
            };

            // Call API to update pre-order
            const response = await updatePreOrderAPI(payload);

            toast.success(response.data.message || t("Pre-order updated successfully"));

            // Redirect to list
            setTimeout(() => {
                history(`${process.env.PUBLIC_URL}/pre-order-list`);
            }, 1500);

        } catch (error) {
            console.error("Error updating pre-order:", error);
            toast.warning(error.response?.data?.message || t("Failed to update pre-order"));
        } finally {
            setLoading(false);
        }
    };

    // Reset to original data
    const handleReset = () => {
        if (originalData) {
            // Reset supplier
            if (originalData.supplierId) {
                // You might want to fetch supplier details again or keep it as is
            }

            // Reset selected items
            if (originalData.preorderData && Array.isArray(originalData.preorderData)) {
                const items = originalData.preorderData.map(item => ({
                    id: item.itemId,
                    brandName: item.brandName,
                    saltName: item.saltName || "",
                    manufacturer: item.manufacturer || "",
                    package: item.package || "",
                    productForm: item.productForm || "",
                    itemId: item.itemId,
                    quantity: item.quantity || 1,
                    notes: ""
                }));
                setSelectedItems(items);

                // Update multi-select value
                const multiSelectItems = items.map(item => ({
                    value: item.id,
                    label: `${item.brandName}`,
                    raw: {
                        brandName: item.brandName,
                        saltName: item.saltName,
                        manufacturer: item.manufacturer,
                        package: item.package,
                        productForm: item.productForm
                    }
                }));
                setMultiSelectValue(multiSelectItems);
            }

            setErrors({});
            toast.info(t("Form reset to original values"));
        }
    };

    const handleSupplierSearchChange = (newValue) => {
        setSearchQuerySupplier(newValue);
    };

    const handleItemSearchChange = (newValue) => {
        setSearchQueryItem(newValue);
    };

    const gotoList = () => {
        history(`${process.env.PUBLIC_URL}/pre-order-list`);
    };

    // Format the multi-select value to show selected items
    const getMultiSelectValue = () => {
        return selectedItems.map(item => {
            const rawItem = itemOptions.find(option => option.value === item.id)?.raw || {};
            return {
                value: item.id,
                label: `${item.brandName}`,
                raw: rawItem
            };
        });
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: selectedPreOrder
            ? `PreOrder_${selectedPreOrder.orderNumber || selectedPreOrder._id}`
            : "PreOrder",
        pageStyle: `
            @page { size: auto; margin: 10mm; }
            body { font-family: Arial; }
        `,
        onAfterPrint: () => setSelectedPreOrder(null),
    });

    if (!preorderId) {
        return (
            <div className="text-center py-5">
                <h3>{t("Pre-order ID is missing")}</h3>
                <Button onClick={gotoList} className="mt-3">
                    {t("Back to List")}
                </Button>
            </div>
        );
    }

    // if (loading && !isDataLoaded) {
    //     return (
    //         <div className="text-center py-5">
    //             <h3>{t("Loading pre-order details...")}</h3>
    //         </div>
    //     );
    // }

    const isDark = document.body.classList.contains("dark-only");
    return (
        <Fragment>
            <Breadcrumbs mainTitle={t("Update Pre Order")} parent={t("Dashboard")} title={t("Update Pre Order")} />
            <Row className="mt-3">
                <Col sm={12}>
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <div>
                                <h5 className="mb-0">{t("Update Pre Order")}</h5>
                                <small className="text-muted">{t("Pre-order ID")}: {preorderId}</small>
                            </div>
                            <button
                                className="btn btn-primary d-flex align-items-center"
                                style={{ color: "white", height: "40px" }}
                                onClick={gotoList}
                            >
                                <ArrowLeft className="me-2" /> {t("Back to List")}
                            </button>
                        </CardHeader>
                        <CardBody>
                            <Form onSubmit={handleUpdatePreOrder}>
                                <Row className="g-3 mb-4">
                                    {/* Supplier Selection */}
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("Supplier")} <span className="text-danger">*</span></Form.Label>
                                            <div className="input-group">
                                                <AsyncSelect
                                                    className="flex-grow-1"
                                                    cacheOptions
                                                    loadOptions={loadSuppliers}
                                                    defaultOptions={[]}
                                                    onChange={handleSelectSupplier}
                                                    onInputChange={handleSupplierSearchChange}
                                                    placeholder={t("Search & select a supplier")}
                                                    noOptionsMessage={() =>
                                                        searchQuerySupplier
                                                            ? t("No suppliers found. Try a different search.")
                                                            : t('Start typing to search...')
                                                    }
                                                    isClearable={true}
                                                    isLoading={loading}
                                                    value={supplier}
                                                    isDisabled={!isDataLoaded}
                                                    theme={(theme) => ({
                                                        ...theme,
                                                        colors: {
                                                            ...theme.colors,
                                                            primary25: isDark ? "#373737" : "#e6f7ffff",

                                                        },
                                                    })}
                                                />
                                            </div>
                                            {errors.supplier && (
                                                <Form.Text className="text-danger">{errors.supplier}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    {/* Items Selection - Only show when supplier is selected */}
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("Select Items")} <span className="text-danger">*</span></Form.Label>
                                            <div className="input-group">
                                                <AsyncSelect
                                                    className="flex-grow-1"
                                                    cacheOptions
                                                    loadOptions={debouncedLoadItems}
                                                    defaultOptions={itemOptions}
                                                    onChange={handleSelectItem}
                                                    onInputChange={handleItemSearchChange}
                                                    placeholder={t("Search items from supplier's catalog")}
                                                    noOptionsMessage={() =>
                                                        searchQueryItem
                                                            ? t("No items found. Try a different search.")
                                                            : t('Start typing to search items...')
                                                    }
                                                    isMulti
                                                    isClearable={true}
                                                    isLoading={isLoadingItems}
                                                    isDisabled={!supplier}
                                                    closeMenuOnSelect={false}
                                                    value={getMultiSelectValue()}
                                                    components={{
                                                        IndicatorSeparator: null
                                                    }}
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '38px'
                                                        })
                                                    }}
                                                    theme={(theme) => ({
                                                        ...theme,
                                                        colors: {
                                                            ...theme.colors,
                                                            primary25: isDark ? "#373737" : "#e6f7ffff",

                                                        },
                                                    })}
                                                />
                                            </div>
                                            {errors.items && (
                                                <Form.Text className="text-danger">{errors.items}</Form.Text>
                                            )}
                                            {itemOptions.length === 0 && supplier && !isLoadingItems && (
                                                <Form.Text className="text-warning">
                                                    {t("No items found for this supplier.")}
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Selected Items Table */}
                                {selectedItems.length > 0 && (
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <div className="border rounded p-3">
                                                <h6 className="mb-3">
                                                    {t("Selected Items")} <span className="badge bg-primary ms-2">{selectedItems.length}</span>
                                                </h6>
                                                <div className="table-responsive">
                                                    <Table bordered hover size="sm">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="text-center" width="5%">#</th>
                                                                <th width="25%">{t("Product")}</th>
                                                                <th width="15%">{t("Manufacturer")}</th>
                                                                <th width="15%">{t("Form")}</th>
                                                                <th width="15%">{t("Package")}</th>
                                                                <th width="10%">{t("Quantity")}</th>
                                                                <th width="5%">{t("Action")}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedItems.map((item, index) => (
                                                                <tr key={item.id}>
                                                                    <td className="text-center">{index + 1}</td>
                                                                    <td>
                                                                        <div>
                                                                            <strong className="d-block">{item.brandName}</strong>
                                                                            <small className="text-muted">{item.saltName}</small>
                                                                        </div>
                                                                    </td>
                                                                    <td>{item.manufacturer}</td>
                                                                    <td>{item.productForm}</td>
                                                                    <td>{item.package}</td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="1"
                                                                            value={item.quantity}
                                                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                                            style={{ width: "80px" }}
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <div className="d-flex gap-2">
                                                                            <button
                                                                                className='success-action-button'
                                                                                id="PrintToolTip"
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();

                                                                                    setPrintItem(item);

                                                                                    setTimeout(() => {
                                                                                        handlePrint();
                                                                                    }, 100);
                                                                                }}
                                                                            >
                                                                                <i className="fa fa-print" style={{ fontSize: "18px" }}></i>
                                                                                <ToolTip
                                                                                    attrToolTip={{
                                                                                        placement: 'top',
                                                                                        isOpen: printTooltipOpen,
                                                                                        target: 'PrintToolTip',
                                                                                        toggle: togglePrintTooltip,
                                                                                    }} >
                                                                                    <strong>{t("Print Order")}</strong>
                                                                                </ToolTip>
                                                                            </button>
                                                                            {printItem && (
                                                                                <div style={{ display: "none" }}>
                                                                                    <PrintablePreOrder
                                                                                        ref={printRef}
                                                                                        preorder={item}
                                                                                        supplier={supplier?.raw}
                                                                                        items={[printItem]}
                                                                                        t={t}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                            <button
                                                                                className='danger-action-button'
                                                                                type="button"
                                                                                id="DeleteToolTip"
                                                                                onClick={() => handleRemoveItem(item.id)}
                                                                            >
                                                                                <i className="fa fa-trash" style={{ fontSize: "18px" }}></i>
                                                                                <ToolTip
                                                                                    attrToolTip={{
                                                                                        placement: 'top',
                                                                                        isOpen: deleteTooltipOpen,
                                                                                        target: 'DeleteToolTip',
                                                                                        toggle: toggleDeleteTooltip,
                                                                                    }} >
                                                                                    <strong>{t("Delete")}</strong>
                                                                                </ToolTip>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {/* Action Buttons */}
                                <Row className="g-3">
                                    <Col md={12}>
                                        <div className="d-flex gap-2 justify-content-end">
                                            {/* <div>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={handleReset}
                                                    disabled={loading || !originalData}
                                                >
                                                    {t("Reset to Original")}
                                                </Button>
                                            </div> */}
                                            <div className="d-flex gap-2">
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={loading || !supplier || selectedItems.length === 0}
                                                >
                                                    {loading ? t("Updating...") : t("Update Pre Order")}
                                                    {selectedItems.length > 0 && (
                                                        <span className="badge bg-white text-primary ms-2">
                                                            {selectedItems.length}
                                                        </span>
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    onClick={gotoList}
                                                    disabled={loading}
                                                >
                                                    {t("Cancel")}
                                                </Button>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

// Debounce utility function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default UpdatePreOrder;